import { supabase } from './supabase';
import type {
  Agent,
  AgentopiaApp,
  AgentRelease,
  AgentResponsibility,
  AgentTask,
  CharacterAppearance,
  CharacterBaseType,
  CharacterProfile,
  CutoverState,
  ModelProvider,
  SafetyState,
  ToolProvider,
  WorkspaceMembership,
} from '../types';

export const CANONICAL_AGENTOPIA_DATA_MODEL = 'mysalri.agentopia_core.v1';

/* Cloudflare V7 previews are visual/read-only. Never change canonical runtime through staging. */
const STAGING_VISUAL_READ_ONLY = typeof window !== 'undefined' && window.location.hostname.endsWith('.agentordinary-review.pages.dev');
function assertNotStagingWrite() { if (STAGING_VISUAL_READ_ONLY) throw new Error('This is a visual staging preview. Live changes are disabled.'); }


const agentSelect =
  'id,owner_id,role_key,official_name,nickname,title,department,status,enabled,profile_state,current_version,revision,draft_config,created_at,updated_at';

const asObject = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, any>)
    : {};

const mapAgent = (row: any): Agent => ({
  id: String(row.id),
  workspace_id: String(row.owner_id),
  role_key: String(row.role_key || ''),
  official_name: String(row.official_name || row.role_key || 'Agent'),
  nickname: row.nickname ? String(row.nickname) : null,
  title: String(row.title || row.role_key || 'AI Employee'),
  department: row.department ? String(row.department) : null,
  status: String(row.status || (row.enabled ? 'idle' : 'paused')),
  enabled: row.enabled === true,
  profile_state: String(row.profile_state || 'draft'),
  current_version: Number(row.current_version || 1),
  revision: Number(row.revision || 0),
  profile: asObject(row.draft_config),
});

async function requireUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data.user?.id) throw new Error('Sign in to the canonical MySalri workforce first.');
  return data.user.id;
}

function assertWorkspace(userId: string, workspaceId: string) {
  if (userId !== workspaceId) {
    throw new Error('This Agentopia session cannot access another owner workspace.');
  }
}

async function invokeSynchronizedRuntimeControl(
  operation: 'global.set' | 'agent.set' | 'responsibility.pause' | 'responsibility.resume',
  payload: Record<string, unknown>,
): Promise<any> {
  const { data, error } = await supabase.functions.invoke('salri-capability-executor', {
    body: {
      bridge_action: 'bridge.runtime.control',
      operation,
      ...payload,
    },
  });
  if (error) throw error;
  if (!data?.ok) {
    const detail = data?.detail ? ': ' + String(data.detail) : '';
    throw new Error(String(data?.error || 'Agentopia runtime control could not be synchronized.') + detail);
  }
  if (data?.synchronized !== true) {
    throw new Error('Agentopia runtime control did not confirm synchronized canonical and private state.');
  }
  return data;
}

export async function getPrimaryMembership(): Promise<WorkspaceMembership> {
  const userId = await requireUserId();
  return { workspace_id: userId, role: 'owner' };
}

export async function listAgents(workspaceId: string): Promise<Agent[]> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  const { data, error } = await supabase
    .from('agentopia_core_agents')
    .select(agentSelect)
    .eq('owner_id', userId)
    .order('official_name');

  if (error) throw error;
  return (data ?? []).map(mapAgent);
}

export async function saveAgent(
  workspaceId: string,
  agent: Agent,
): Promise<Agent> {
  assertNotStagingWrite();
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  const { data, error } = await supabase.rpc('agentopia_owner_update_core_agent_profile', {
    p_agent_id: agent.id,
    p_expected_revision: agent.revision,
    p_official_name: agent.official_name.trim(),
    p_nickname: agent.nickname?.trim() || null,
    p_title: agent.title.trim(),
    p_department: agent.department?.trim() || null,
    p_profile: agent.profile,
  });

  if (error) {
    if (String(error.message || '').includes('agent_revision_conflict')) {
      throw new Error(
        'This profile changed somewhere else. Refresh Agentopia, review the newest version, and save again.',
      );
    }
    throw error;
  }
  if (!data) throw new Error('Canonical Agentopia profile update returned no employee.');
  return mapAgent(data);
}

export async function listAgentReleases(
  workspaceId: string,
  agentId?: string,
): Promise<AgentRelease[]> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  let query = supabase
    .from('agentopia_core_releases')
    .select('id,owner_id,agent_id,version,config_hash,manifest,published_at')
    .eq('owner_id', userId)
    .order('published_at', { ascending: false });

  if (agentId) query = query.eq('agent_id', agentId);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    id: String(row.id),
    workspace_id: String(row.owner_id),
    agent_id: String(row.agent_id),
    version: Number(row.version || 1),
    revision: Number(row.manifest?.revision || row.version || 1),
    schema_name: 'agentopia_core',
    snapshot: asObject(row.manifest),
    release_hash: String(row.config_hash || ''),
    published_at: String(row.published_at),
  }));
}

export async function listResponsibilities(
  workspaceId: string,
): Promise<AgentResponsibility[]> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  const { data, error } = await supabase
    .from('agentopia_core_responsibilities')
    .select(
      'id,owner_id,agent_id,responsibility_key,name,objective,mode,status,priority,source_trigger_id,current_action,next_action,resume_condition,checkpoint,last_task_id,last_started_at,last_completed_at,next_review_at,max_parallel,metadata,created_at,updated_at',
    )
    .eq('owner_id', userId)
    .order('priority', { ascending: false })
    .order('updated_at', { ascending: false });
  if (error) throw error;

  return (data ?? []).map((row: any) => ({
    id: String(row.id),
    workspace_id: String(row.owner_id),
    agent_id: String(row.agent_id),
    responsibility_key: String(row.responsibility_key),
    name: String(row.name),
    objective: String(row.objective),
    mode: String(row.mode),
    status: String(row.status),
    priority: Number(row.priority || 50),
    source_trigger_id: row.source_trigger_id ? String(row.source_trigger_id) : null,
    current_action: row.current_action ? String(row.current_action) : null,
    next_action: row.next_action ? String(row.next_action) : null,
    resume_condition: row.resume_condition ? String(row.resume_condition) : null,
    checkpoint: row.checkpoint ? String(row.checkpoint) : null,
    last_task_id: row.last_task_id ? String(row.last_task_id) : null,
    last_started_at: row.last_started_at ? String(row.last_started_at) : null,
    last_completed_at: row.last_completed_at ? String(row.last_completed_at) : null,
    next_review_at: row.next_review_at ? String(row.next_review_at) : null,
    max_parallel: Number(row.max_parallel || 1),
    metadata: asObject(row.metadata),
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
  }));
}

export async function pauseResponsibility(
  responsibilityId: string,
  reason = 'Paused by owner from Live Work.',
): Promise<void> {
  assertNotStagingWrite();
  await invokeSynchronizedRuntimeControl('responsibility.pause', {
    responsibility_id: responsibilityId,
    reason,
  });
}

export async function resumeResponsibility(
  responsibilityId: string,
  reason = 'Resumed by owner from Live Work.',
): Promise<string | null> {
  assertNotStagingWrite();
  const data = await invokeSynchronizedRuntimeControl('responsibility.resume', {
    responsibility_id: responsibilityId,
    reason,
  });
  return data?.canonical ? String(data.canonical) : null;
}

export async function setResponsibilityPriority(
  responsibilityId: string,
  priority: number,
): Promise<void> {
  assertNotStagingWrite();
  const { data, error } = await supabase.rpc('agentopia_owner_set_responsibility_priority', {
    p_responsibility_id: responsibilityId,
    p_priority: Math.max(1, Math.min(100, Math.round(priority))),
  });
  if (error) throw error;
  if (data !== true) throw new Error('Responsibility priority update was not confirmed.');
}

export async function setGlobalRuntimeState(
  state: 'active' | 'paused',
  reason = state === 'paused' ? 'Paused by owner from Guardrail.' : 'Resumed by owner from Guardrail.',
): Promise<void> {
  assertNotStagingWrite();
  await invokeSynchronizedRuntimeControl('global.set', { state, reason });
}

export async function setAgentActivation(
  workspaceId: string,
  agentId: string,
  state: 'active' | 'paused' | 'dormant',
  reason = state === 'active' ? 'Activated by owner from Workforce.' : 'Paused by owner from Workforce.',
): Promise<Agent> {
  assertNotStagingWrite();
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  await invokeSynchronizedRuntimeControl('agent.set', {
    agent_id: agentId,
    state,
    reason,
  });

  const { data, error: readError } = await supabase
    .from('agentopia_core_agents')
    .select(agentSelect)
    .eq('owner_id', userId)
    .eq('id', agentId)
    .single();
  if (readError) throw readError;
  return mapAgent(data);
}

export async function listTasks(workspaceId: string): Promise<AgentTask[]> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  const [workResult, agents] = await Promise.all([
    supabase
      .from('work_items')
      .select(
        'id,user_id,source_type,source_id,title,description,status,agent_name,owner_action,blocker,started_at,completed_at,metadata,created_at,updated_at,lifecycle_stage,owner_action_ready',
      )
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(100),
    listAgents(workspaceId),
  ]);
  if (workResult.error) throw workResult.error;

  const byName = new Map<string, string>();
  for (const agent of agents) {
    for (const name of [agent.official_name, agent.nickname, agent.role_key]) {
      if (name) byName.set(String(name).trim().toLowerCase(), agent.id);
    }
  }

  return (workResult.data ?? []).map((row: any) => {
    const metadata = asObject(row.metadata);
    const lifecycle = String(row.lifecycle_stage || row.status || '').toLowerCase();
    const ownerNeeded =
      row.owner_action_ready === true ||
      lifecycle === 'need_you' ||
      lifecycle === 'waiting_on_owner' ||
      Boolean(row.owner_action);
    const agentId = byName.get(String(row.agent_name || '').trim().toLowerCase()) ?? null;
    return {
      id: String(row.id),
      workspace_id: userId,
      agent_id: agentId,
      title: String(row.title || 'Work item'),
      objective: row.description ? String(row.description) : null,
      status: String(row.lifecycle_stage || row.status || 'queued'),
      priority: Number(metadata.priority || 50),
      requires_approval: ownerNeeded,
      blocker: row.blocker ? String(row.blocker) : row.owner_action ? String(row.owner_action) : null,
      scheduled_at: null,
      started_at: row.started_at ? String(row.started_at) : null,
      completed_at: row.completed_at ? String(row.completed_at) : null,
      attempt_count: Number(metadata.attempt_count || 0),
      max_attempts: Number(metadata.max_attempts || 3),
      last_error: metadata.last_error ? String(metadata.last_error) : null,
      source_system: String(row.source_type || 'salri-canonical-work-item'),
      source_id: row.source_id ? String(row.source_id) : null,
      metadata,
      created_at: String(row.created_at),
      updated_at: String(row.updated_at),
    } satisfies AgentTask;
  });
}

export async function listApps(workspaceId: string): Promise<AgentopiaApp[]> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  const { data, error } = await supabase
    .from('agentopia_core_apps')
    .select('id,owner_id,app_key,name,kind,status,metadata')
    .eq('owner_id', userId)
    .order('name');
  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    id: String(row.id),
    workspace_id: userId,
    app_key: String(row.app_key),
    name: String(row.name),
    kind: String(row.kind),
    status: String(row.status),
    metadata: asObject(row.metadata),
  }));
}

export async function listToolProviders(
  workspaceId: string,
): Promise<ToolProvider[]> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  const { data, error } = await supabase
    .from('capability_runtime_status')
    .select(
      'capability_key,provider,runtime_mode,connected,healthy,spend_allowed,last_heartbeat_at,last_healthcheck_at,last_error,metadata',
    )
    .order('capability_key');
  if (error) throw error;
  return (data ?? []).map((row: any) => {
    const metadata = asObject(row.metadata);
    return {
      id: String(row.capability_key),
      workspace_id: userId,
      provider_key: String(row.capability_key),
      display_name: String(metadata.display_name || row.capability_key),
      transport: String(row.runtime_mode || 'runtime'),
      enabled: row.connected === true,
      health_status: row.healthy === true ? 'healthy' : row.connected === true ? 'degraded' : 'disconnected',
      free_only: row.spend_allowed !== true,
      allows_paid_fallback: row.spend_allowed === true,
      risk_class: String(metadata.risk_class || 'runtime-governed'),
      capabilities: metadata,
      last_health_check_at: row.last_healthcheck_at || row.last_heartbeat_at || null,
      last_health_detail: row.last_error ? String(row.last_error) : null,
    } satisfies ToolProvider;
  });
}

export async function listModelProviders(
  workspaceId: string,
): Promise<ModelProvider[]> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  // Agentopia's original login is unchanged. Provider health comes from the
  // new SALRI project's read-only provider-health endpoint, not legacy records.
  const response = await fetch(
    'https://zbucxeuqehtledwnbtwh.supabase.co/functions/v1/salri-provider-health',
    { method: 'GET', cache: 'no-store', headers: { Accept: 'application/json' } },
  );
  if (!response.ok) throw new Error('Fresh SALRI provider health is unavailable.');
  const payload: any = await response.json();
  if (payload?.ok !== true || !Array.isArray(payload.models)) {
    throw new Error('Fresh SALRI provider status could not be verified.');
  }
  return payload.models.map((row: any) => ({
    id: String(row.provider) + ':' + String(row.model),
    workspace_id: userId,
    provider_key: String(row.provider) + ':' + String(row.model),
    display_name: String(row.provider) + ' / ' + String(row.model),
    enabled: row.enabled === true,
    health_status: String(row.health_status || 'unverified'),
    free_only: row.free_only === true,
    allows_paid_fallback: false,
    capabilities: {},
    priority: 100,
    last_health_check_at: row.last_health_check_at ?? null,
    last_health_detail: 'Fresh SALRI read-only evidence, fetched ' + String(payload.checked_at || 'unknown')
      + '; latest actual call ' + String(row.last_real_call_at || 'not recorded'),
  } satisfies ModelProvider));
}
export async function getSafetyState(
  workspaceId: string,
): Promise<SafetyState> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  const { data, error } = await supabase
    .from('agentopia_core_runtime_policy')
    .select(
      'owner_id,global_state,safety_armed,pause_reason,max_identical_failures,max_identical_tests,metadata,updated_at',
    )
    .eq('owner_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) {
    return {
      workspace_id: userId,
      armed: true,
      global_paused: true,
      pause_reason: 'Canonical Phase B runtime policy is not initialized.',
      tripped_at: null,
      tripped_by: null,
      last_event_at: null,
      max_identical_failures: 0,
      max_identical_tests: 0,
      hard_error_immediate: true,
      updated_at: new Date(0).toISOString(),
    };
  }
  const metadata = asObject(data.metadata);
  return {
    workspace_id: userId,
    armed: data.safety_armed !== false,
    global_paused: data.global_state !== 'active',
    pause_reason: data.pause_reason ? String(data.pause_reason) : null,
    tripped_at: metadata.tripped_at ? String(metadata.tripped_at) : null,
    tripped_by: metadata.tripped_by ? String(metadata.tripped_by) : null,
    last_event_at: metadata.last_event_at ? String(metadata.last_event_at) : String(data.updated_at),
    max_identical_failures: Number(data.max_identical_failures || 0),
    max_identical_tests: Number(data.max_identical_tests || 0),
    hard_error_immediate: true,
    updated_at: String(data.updated_at),
  };
}

export async function listCutovers(
  workspaceId: string,
): Promise<CutoverState[]> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  return [];
}

export async function listCharacterProfiles(
  workspaceId: string,
): Promise<CharacterProfile[]> {
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  const { data, error } = await supabase
    .from('agentopia_core_character_profiles')
    .select('id,owner_id,agent_id,base_type,transparent_background,status,appearance,created_at,updated_at')
    .eq('owner_id', userId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    id: String(row.id),
    workspace_id: String(row.owner_id),
    agent_id: String(row.agent_id),
    base_type: row.base_type as CharacterBaseType,
    transparent_background: row.transparent_background !== false,
    status: row.status === 'ready' ? 'ready' : 'draft',
    appearance: asObject(row.appearance) as CharacterAppearance,
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
  }));
}

export async function saveCharacterProfile(
  workspaceId: string,
  agentId: string,
  baseType: CharacterBaseType,
  appearance: CharacterAppearance,
): Promise<CharacterProfile> {
  assertNotStagingWrite();
  const userId = await requireUserId();
  assertWorkspace(userId, workspaceId);
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('agentopia_core_character_profiles')
    .upsert(
      {
        owner_id: userId,
        agent_id: agentId,
        base_type: baseType,
        transparent_background: true,
        status: 'draft',
        appearance,
        updated_at: now,
      },
      { onConflict: 'owner_id,agent_id' },
    )
    .select('id,owner_id,agent_id,base_type,transparent_background,status,appearance,created_at,updated_at')
    .single();

  if (error) throw error;
  return {
    id: String(data.id),
    workspace_id: String(data.owner_id),
    agent_id: String(data.agent_id),
    base_type: data.base_type as CharacterBaseType,
    transparent_background: data.transparent_background !== false,
    status: data.status === 'ready' ? 'ready' : 'draft',
    appearance: asObject(data.appearance) as CharacterAppearance,
    created_at: String(data.created_at),
    updated_at: String(data.updated_at),
  };
}

